/**
 * Optimistic PDP media switching.
 *
 * The native variant-picker still owns the canonical Shopify section fetch and
 * reconciliation. This module only updates the existing first gallery image
 * from the server-rendered variant/media map before that fetch completes.
 */

const MAP_SELECTOR = 'script[data-bmp-variant-media-map]';
const PICKER_SELECTOR = 'variant-picker[data-template-product-match="true"]';
const IMAGE_SELECTOR = 'img.product-media__image';
const INSTALL_KEY = '__moftailInstantVariantMediaInstalled';

/** @returns {{ options: string[]; variants: Array<{ id: string; options: string[]; mediaId?: string }>; media: Array<{ id: string; src: string; srcset: string }> } | null} */
function readMediaMap() {
  const script = document.querySelector(MAP_SELECTOR);
  if (!script) return null;

  try {
    const parsed = JSON.parse(script.textContent || '');
    if (!parsed || !Array.isArray(parsed.variants) || !Array.isArray(parsed.media)) return null;
    return parsed;
  } catch (error) {
    console.warn('[instant-variant-media] Invalid media map:', error);
    return null;
  }
}

/** @param {HTMLElement} picker */
function selectedValues(picker) {
  const values = [];
  const fields = Array.from(picker.querySelectorAll('select, fieldset'));

  for (const field of fields) {
    const index = Number.parseInt(field.getAttribute('data-fieldset-index') || '0', 10);
    const selected =
      field instanceof HTMLSelectElement
        ? field.selectedOptions[0]
        : field.querySelector('input:checked') || field.querySelector('input[data-current-checked="true"], option[selected]');
    if (selected instanceof HTMLElement) {
      values[index] = selected.value;
    }
  }

  return values;
}

/** @param {HTMLElement} picker @param {ReturnType<typeof readMediaMap>} map */
function resolveVariant(picker, map) {
  if (!map) return null;
  const values = selectedValues(picker);
  if (!values.some((value) => value !== undefined)) return null;

  return (
    map.variants.find(
      (variant) =>
        Array.isArray(variant.options) &&
        variant.options.length >= values.length &&
        variant.options.every((value, index) => values[index] === undefined || value === values[index])
    ) || null
  );
}

/** @returns {HTMLImageElement | null} */
function findVisibleGalleryImage() {
  const gallery = document.querySelector('media-gallery');
  if (!gallery) return null;

  const images = Array.from(gallery.querySelectorAll(IMAGE_SELECTOR));
  return (
    images.find((image) => {
      const slide = image.closest('slideshow-slide');
      if (slide?.getAttribute('aria-hidden') === 'true') return false;
      const rect = image.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }) || images[0] || null
  );
}

/** @param {HTMLImageElement} image @param {{ src: string; srcset: string }} variant */
function applyImage(image, variant) {
  if (!variant.src) return;

  image.setAttribute('srcset', variant.srcset);
  image.setAttribute('src', variant.src);
  image.dataset.bmpOptimisticVariantMedia = 'true';
}

/** @param {HTMLElement} picker @param {{ mediaId?: string; src: string; srcset: string }} variant */
function selectExistingGalleryMedia(picker, variant) {
  const image = findVisibleGalleryImage();
  if (!image) return false;

  // Keep the existing slideshow as the canonical gallery controller when the
  // target media is already rendered in the initial gallery.
  const mediaGallery = image.closest('media-gallery');
  const targetMedia = variant.mediaId
    ? mediaGallery?.querySelector(`[data-media-id="${CSS.escape(String(variant.mediaId))}"]`)
    : null;
  const targetSlide = targetMedia?.closest('slideshow-slide');
  const slideshow = mediaGallery?.querySelector('slideshow-component');
  const slides = slideshow ? Array.from(slideshow.querySelectorAll('slideshow-slide')) : [];
  const targetIndex = targetSlide ? slides.indexOf(targetSlide) : -1;

  if (slideshow && targetIndex >= 0 && typeof slideshow.select === 'function') {
    slideshow.select(targetIndex, undefined, { animate: false });
    return true;
  }

  // Variant-scoped galleries may not contain the target slide yet. In that
  // case update the existing visible image only; the official section fetch
  // will replace/reconcile the gallery once its canonical response arrives.
  applyImage(image, variant);
  return true;
}

function install() {
  if (window[INSTALL_KEY]) return;
  window[INSTALL_KEY] = true;

  let generation = 0;
  document.addEventListener(
    'change',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const picker = event.target.closest(PICKER_SELECTOR);
      if (!picker || event.target.closest('product-card, quick-add-dialog')) return;

      const map = readMediaMap();
      const resolvedVariant = resolveVariant(picker, map);
      if (!resolvedVariant) return;
      const variantMedia = map?.media.find((media) => String(media.id) === String(resolvedVariant.mediaId));
      if (!variantMedia) return;

      const currentGeneration = ++generation;
      if (!selectExistingGalleryMedia(picker, { ...resolvedVariant, ...variantMedia })) return;

      // Mark only the latest optimistic selection for diagnostics. The native
      // VariantPicker aborts stale fetches and remains the source of truth.
      queueMicrotask(() => {
        if (currentGeneration === generation) {
          document.dispatchEvent(
            new CustomEvent('moftail:optimistic-media-switch', {
              detail: { variantId: resolvedVariant.id, mediaId: resolvedVariant.mediaId },
            })
          );
        }
      });
    },
    true
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', install, { once: true });
} else {
  install();
}
